/**
 * SB6-ANA-DA-03 — flat list of every globally-defined defect class-model.
 *
 *   GET /api/lyse/v1/class-models  → { items: ClassModelListItem[] }
 *
 * The analysis-page class-model picker reads this list directly so the
 * dropdown is decoupled from the active catalogue's `class_model_id`
 * default (Christian, 2026-05-29):
 *
 *   "the class model ID parameter [on the catalogue] just defines the
 *    default class model. But in the application, in the UI, we must
 *    be able to choose any of the known class models — Aero Enterprise
 *    standard with class model None, or Aero Enterprise standard with
 *    severity 5-grade, etc."
 *
 * Cached for the session — class-model rows are global reference data
 * and don't change without a deploy.
 */
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { components } from "@aeriosiq/types";

export type ClassModelListItem = components["schemas"]["ClassModelListItem"];
export type ListClassModelsResponse =
  components["schemas"]["ListClassModelsResponse"];

export const classModelsKeys = {
  all: ["class-models"] as const,
  list: () => [...classModelsKeys.all, "list"] as const,
};

const STALE_TIME_MS = 60 * 60_000; // 1 hour — global reference data
const GC_TIME_MS = 24 * 60 * 60_000; // 24 hours

export function useClassModelsQuery() {
  const api = useApi();
  return useQuery<ListClassModelsResponse>({
    queryKey: classModelsKeys.list(),
    queryFn: async () => {
      const res = await api.get<ListClassModelsResponse>("/class-models");
      return res.data;
    },
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: false,
    meta: { failureToast: "Class models unavailable" },
  });
}
